import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { BET_LABEL, SPORT_META, formatOdds, scoreToUnits, bettingDayBounds, todayBettingDatePT } from "@/lib/picks";
import { toast } from "sonner";
import { Sparkles, Check, X, Send, Trophy, Star, EyeOff, Plus, Pencil, Upload, Calendar as CalendarIcon, Search, Trash2, RotateCcw, AlertTriangle, Lock, Info } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import ParlayOfDayManager from "@/components/ParlayOfDayManager";
import ApiTestLab from "@/components/ApiTestLab";
import AutoTop5Generator from "@/components/AutoTop5Generator";
import AdminOverview from "@/components/admin/AdminOverview";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink, Settings as SettingsIcon, FileText, Users as UsersIcon, ChevronDown, ChevronRight } from "lucide-react";
import { slateLabel, slateRelative, ROLLOVER_HINT } from "@/lib/slate";

const SPORTS = ["NBA", "NFL", "MLB", "NHL", "SOCCER"] as const;
const BET_TYPES = [
  { value: "player_prop", label: "Player Prop" },
  { value: "moneyline", label: "Moneyline" },
  { value: "spread", label: "Spread" },
  { value: "game_total", label: "Total" },
  { value: "team_total", label: "Team Total" },
  { value: "parlay", label: "Parlay" },
] as const;
const STATUSES = ["draft", "approved", "published", "rejected"] as const;
const RESULTS = ["pending", "won", "lost", "pushed"] as const;
const RISKS = ["low", "medium", "high"] as const;

type SportRow = { id: string; code: string };
type LeagueRow = { id: string; sport_id: string; name: string; short_name: string | null };
type TeamRow = { id: string; league_id: string; name: string };
type PlayerRow = { id: string; team_id: string | null; full_name: string };

type FormState = {
  id?: string;
  updated_at?: string | null;
  sport_code: string;
  league_id: string;
  game_id: string;
  home_team_id: string;
  away_team_id: string;
  game_start_time: string;
  bet_type: string;
  player_id: string;
  player_name: string;
  title: string;
  selection: string;
  teams: string;
  line: string;
  odds: string;
  sportsbook: string;
  confidence_score: string;
  unit_rating: string;
  risk: string;
  ai_summary: string;
  ai_reasoning: string;
  risk_factors: string;
  status: string;
  is_top5: boolean;
  is_free_pick: boolean;
  betting_date: string;
  // Score breakdown
  market_edge: number;       // 0-30 (auto)
  line_movement: number;     // 0-15 (auto)
  line_movement_neutral: boolean; // true when default/insufficient snapshots
  injury_impact: number;     // 0-15 (manual)
  recent_form: number;       // 0-15 (manual)
  matchup_edge: number;      // 0-15 (manual)
  data_quality: number;      // 0-10 (manual)
  manual_reviewed: boolean;
  units_override: string;    // "" = auto, otherwise 1-5
};

type FormErrors = Partial<Record<
  "sport_code" | "bet_type" | "title" | "odds" | "game_start_time" | "confidence_score" | "unit_rating" | "line",
  string
>>;

const emptyForm = (): FormState => ({
  sport_code: "NBA", league_id: "", game_id: "", home_team_id: "", away_team_id: "",
  game_start_time: "", bet_type: "moneyline", player_id: "", player_name: "",
  title: "", selection: "", teams: "", line: "", odds: "", sportsbook: "",
  confidence_score: "60", unit_rating: "2", risk: "medium",
  ai_summary: "", ai_reasoning: "", risk_factors: "",
  status: "draft", is_top5: false, is_free_pick: false,
  betting_date: todayBettingDatePT(),
  market_edge: 0, line_movement: 7, line_movement_neutral: true,
  injury_impact: 0, recent_form: 0, matchup_edge: 0, data_quality: 0,
  manual_reviewed: false,
  units_override: "",
});

// Compute totals from breakdown
function computeTotals(f: FormState) {
  const auto = (Number(f.market_edge) || 0) + (Number(f.line_movement) || 0); // 0-45
  const manual = (Number(f.injury_impact) || 0) + (Number(f.recent_form) || 0)
    + (Number(f.matchup_edge) || 0) + (Number(f.data_quality) || 0); // 0-55
  const total = Math.max(0, Math.min(100, auto + manual));
  // Map 0-100 to units 1-5
  const units = total >= 85 ? 5 : total >= 70 ? 4 : total >= 55 ? 3 : total >= 40 ? 2 : 1;
  return { auto, manual, total, units };
}

export default function Admin() {
  const location = useLocation();
  const DEBUG_MODAL = typeof window !== "undefined" && window.localStorage.getItem("aleaedge.debug.adminModal") === "1";
  const [sports, setSports] = useState<SportRow[]>([]);
  const [leagues, setLeagues] = useState<LeagueRow[]>([]);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [picks, setPicks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);


  // Form dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterSport, setFilterSport] = useState<string>("all");
  const [filterBetType, setFilterBetType] = useState<string>("all");
  const [filterResult, setFilterResult] = useState<string>("all");
  const [filterDate, setFilterDate] = useState<string>(""); // yyyy-mm-dd, filters created_at
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<"created_at" | "confidence_score" | "game_start_time">("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [savingState, setSavingState] = useState<null | "draft" | "approved" | "published">(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [draftRestoredNotice, setDraftRestoredNotice] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const draftSaveTimer = useRef<number | null>(null);
  const suppressDraftSaveRef = useRef(false);
  const modalSessionKey = "aleaedge.admin.modalSession";
  const modalTtlMs = 24 * 60 * 60 * 1000;

  // Confirmation dialog
  const [confirmAction, setConfirmAction] = useState<null | {
    title: string;
    description: string;
    confirmLabel: string;
    variant?: "destructive" | "default";
    onConfirm: () => any;
  }>(null);

  async function loadRefs() {
    const [{ data: s }, { data: l }, { data: t }, { data: pl }] = await Promise.all([
      supabase.from("sports").select("id, code"),
      supabase.from("leagues").select("id, sport_id, name, short_name"),
      supabase.from("teams").select("id, league_id, name"),
      supabase.from("players").select("id, team_id, full_name"),
    ]);
    setSports((s ?? []) as SportRow[]);
    setLeagues((l ?? []) as LeagueRow[]);
    setTeams((t ?? []) as TeamRow[]);
    setPlayers((pl ?? []) as PlayerRow[]);
  }

  async function loadPicks() {
    setLoading(true);
    let q = supabase.from("picks").select("*, sports(code), leagues(short_name, name)").order("created_at", { ascending: false }).limit(500);
    const { data, error } = await q;
    if (error) toast.error(error.message);
    setPicks(data ?? []);
    setLoading(false);
  }

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { if (sports.length) loadPicks(); /* eslint-disable-next-line */ }, [sports.length]);
  useEffect(() => {
    const handler = () => loadPicks();
    window.addEventListener("picks:refresh", handler);
    return () => window.removeEventListener("picks:refresh", handler);
  }, []);

  const sportLeagues = useMemo(() => {
    const sid = sports.find((s) => s.code === form.sport_code)?.id;
    return leagues.filter((l) => l.sport_id === sid);
  }, [form.sport_code, sports, leagues]);

  const leagueTeams = useMemo(() => teams.filter((t) => t.league_id === form.league_id), [teams, form.league_id]);

  const filteredPicks = useMemo(() => {
    const list = picks.filter((p) => {
      if (showDeleted ? !p.deleted_at : !!p.deleted_at) return false;
      if (filterStatus !== "all" && p.status !== filterStatus) return false;
      if (filterSport !== "all" && p.sports?.code !== filterSport) return false;
      if (filterBetType !== "all" && p.bet_type !== filterBetType) return false;
      if (filterResult !== "all" && p.result !== filterResult) return false;
      if (filterDate) {
        const d = p.created_at ? new Date(p.created_at) : null;
        if (!d) return false;
        const ymd = format(d, "yyyy-MM-dd");
        if (ymd !== filterDate) return false;
      }
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!(p.title?.toLowerCase().includes(q) || p.teams?.toLowerCase().includes(q))) return false;
      }
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = sortBy === "confidence_score" ? Number(a[sortBy] ?? 0) : new Date(a[sortBy] ?? 0).getTime();
      const bv = sortBy === "confidence_score" ? Number(b[sortBy] ?? 0) : new Date(b[sortBy] ?? 0).getTime();
      return (av - bv) * dir;
    });
  }, [picks, filterStatus, filterSport, filterBetType, filterResult, filterDate, filterSearch, showDeleted, sortBy, sortDir]);

  // ---------- ACTIONS ----------
  async function logAction(pick_id: string, action: string) {
    const { data: u } = await supabase.auth.getUser();
    if (u.user) await supabase.from("admin_actions").insert({ admin_id: u.user.id, pick_id, action });
  }

  async function quickUpdate(id: string, patch: any, action?: string) {
    const { error } = await supabase.from("picks").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    if (action) await logAction(id, action);
    toast.success("Updated");
    loadPicks();
  }

  async function settle(id: string, result: "won" | "lost" | "pushed", units: number, odds: number | null) {
    const payout = odds == null ? 1 : odds > 0 ? odds / 100 : 100 / Math.abs(odds);
    const delta = result === "won" ? units * payout : result === "lost" ? -units : 0;
    const settledAt = new Date().toISOString();
    const unitsDelta = Number(delta.toFixed(2));

    const { data: authData } = await supabase.auth.getUser();
    const { error: pickError } = await supabase.from("picks").update({ result, units_delta: unitsDelta }).eq("id", id);
    if (pickError) return toast.error(pickError.message);

    const { error: resultError } = await supabase.from("pick_results").upsert({
      pick_id: id,
      result,
      units_delta: unitsDelta,
      settled_by: authData.user?.id ?? null,
      settled_at: settledAt,
    }, { onConflict: "pick_id" });
    if (resultError) return toast.error(resultError.message);

    await logAction(id, `settled:${result}`);
    toast.success("Updated");
    loadPicks();
  }

  async function softDelete(id: string) {
    await quickUpdate(id, { deleted_at: new Date().toISOString() }, "soft_delete");
  }
  async function restorePick(id: string) {
    await quickUpdate(id, { deleted_at: null }, "restore");
  }

  async function setFreePick(id: string, current: boolean) {
    if (current) return quickUpdate(id, { is_free_pick: false }, "clear_free_pick");
    const bettingDate = todayBettingDatePT();
    const { data: todays } = await supabase.from("picks").select("id").eq("is_free_pick", true)
      .filter("betting_date", "eq", bettingDate);
    const others = (todays ?? []).map((p: any) => p.id).filter((x) => x !== id);
    if (others.length) await supabase.from("picks").update({ is_free_pick: false }).in("id", others);
    await quickUpdate(id, { is_free_pick: true }, "set_free_pick");
  }

  async function publishToday() {
    setPublishing(true);
    try {
      const { data: approved, error } = await supabase.from("picks").select("id").eq("status", "approved");
      if (error) throw error;
      const ids = (approved ?? []).map((p: any) => p.id);
      if (!ids.length) { toast.info("No approved picks to publish."); return; }
      const { error: upErr } = await supabase.from("picks").update({
        status: "published", published_at: new Date().toISOString(),
      }).in("id", ids);
      if (upErr) throw upErr;
      const { data: u } = await supabase.auth.getUser();
      if (u.user) await supabase.from("admin_actions").insert(ids.map((pick_id) => ({ admin_id: u.user!.id, pick_id, action: "publish" })));
      toast.success(`Published ${ids.length} pick${ids.length === 1 ? "" : "s"}`);
      loadPicks();
    } catch (e: any) {
      toast.error(e.message ?? "Publish failed");
    } finally {
      setPublishing(false);
    }
  }

  // ---------- FORM ----------
  const draftKeyFor = (pickId?: string | null) => pickId ? `aleaedge.admin.pickDraft.${pickId}` : "aleaedge.admin.pickDraft.new";
  const clearDraft = (pickId?: string | null) => localStorage.removeItem(draftKeyFor(pickId));
  const hasUnsavedChanges = open && localStorage.getItem(draftKeyFor(form.id)) !== null;

  function saveModalSession(next: { open: boolean; mode: "new" | "edit" | null; pickId: string | null }) {
    const payload = { ...next, path: location.pathname, updatedAt: Date.now() };
    localStorage.setItem(modalSessionKey, JSON.stringify(payload));
    if (DEBUG_MODAL) console.info("[admin-modal] session saved", payload);
  }

  function clearModalSession() {
    localStorage.removeItem(modalSessionKey);
    if (DEBUG_MODAL) console.info("[admin-modal] session cleared");
  }

  function openCreate() {
    setErrors({});
    const saved = localStorage.getItem(draftKeyFor(null));
    if (saved) {
      try {
        setForm({ ...emptyForm(), ...(JSON.parse(saved) as Partial<FormState>), id: undefined, updated_at: null });
        setDraftRestoredNotice("Unsaved draft restored.");
      } catch {
        setForm(emptyForm());
      }
    } else {
      setForm(emptyForm());
    }
    saveModalSession({ open: true, mode: "new", pickId: null });
    setOpen(true);
  }

  async function openEdit(p: any) {
    setErrors({});
    // Try to load existing breakdown
    const { data: bd } = await supabase
      .from("pick_score_breakdowns")
      .select("*")
      .eq("pick_id", p.id)
      .maybeSingle();
    const hasBreakdown = !!bd;
    const baseForm: FormState = {
      id: p.id,
      updated_at: p.updated_at ?? null,
      sport_code: p.sports?.code ?? "NBA",
      league_id: p.league_id ?? "",
      game_id: p.game_id ?? "",
      home_team_id: "",
      away_team_id: "",
      game_start_time: p.game_start_time ? new Date(p.game_start_time).toISOString().slice(0, 16) : "",
      bet_type: p.bet_type,
      player_id: p.player_id ?? "",
      player_name: "",
      title: p.title ?? "",
      selection: "",
      teams: p.teams ?? "",
      line: p.line?.toString() ?? "",
      odds: p.odds?.toString() ?? "",
      sportsbook: p.sportsbook ?? "",
      confidence_score: p.confidence_score?.toString() ?? "60",
      unit_rating: p.unit_rating?.toString() ?? "2",
      risk: p.risk ?? "medium",
      ai_summary: p.ai_summary ?? "",
      ai_reasoning: p.ai_reasoning ?? "",
      risk_factors: p.risk_factors ?? "",
      status: p.status,
      is_top5: !!p.is_top5,
      is_free_pick: !!p.is_free_pick,
      betting_date: p.betting_date ?? todayBettingDatePT(),
      market_edge: bd?.line_value ?? Math.min(30, Math.round((Number(p.confidence_score) || 0) * 0.30)),
      line_movement: bd?.market_movement ?? 7,
      line_movement_neutral: !bd || (bd.market_movement === 7),
      injury_impact: bd?.injury_impact ?? 0,
      recent_form: bd?.recent_form ?? 0,
      matchup_edge: bd?.matchup_edge ?? 0,
      data_quality: bd?.risk_adjustment ?? 0,
      manual_reviewed: hasBreakdown && (bd.injury_impact + bd.recent_form + bd.matchup_edge + bd.risk_adjustment) > 0,
      units_override: p.unit_rating != null ? String(p.unit_rating) : "",
    };
    const saved = localStorage.getItem(draftKeyFor(p.id));
    if (saved) {
      try {
        setForm({ ...baseForm, ...(JSON.parse(saved) as Partial<FormState>), id: p.id, updated_at: p.updated_at ?? null });
        setDraftRestoredNotice("Unsaved draft restored.");
      } catch {
        setForm(baseForm);
      }
    } else {
      setForm(baseForm);
    }
    saveModalSession({ open: true, mode: "edit", pickId: p.id });
    setOpen(true);
  }

  async function aiAssist() {
    setAiBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate_ai_reasoning", {
        body: {
          persist: false,
          sport: form.sport_code,
          bet_type: form.bet_type,
          context: {
            title: form.title, teams: form.teams, line: form.line, odds: form.odds,
            selection: form.selection, player_name: form.player_name,
          },
        },
      });
      if (error) throw error;
      const s = (data as any)?.suggestion;
      if (!s) throw new Error("No suggestion returned");
      setForm((f) => ({
        ...f,
        title: f.title || s.title || "",
        teams: f.teams || s.teams || "",
        line: f.line || (s.line != null ? String(s.line) : ""),
        odds: f.odds || (s.odds != null ? String(s.odds) : ""),
        sportsbook: f.sportsbook || s.sportsbook || "",
        ai_summary: s.ai_summary ?? f.ai_summary,
        ai_reasoning: s.ai_reasoning ?? f.ai_reasoning,
        risk_factors: s.risk_factors ?? f.risk_factors,
        confidence_score: String(s.confidence_score ?? f.confidence_score),
        unit_rating: String(s.unit_rating ?? f.unit_rating),
        risk: s.risk ?? f.risk,
      }));
      toast.success("AI suggestion applied");
    } catch (e: any) {
      toast.error(e.message ?? "AI assist failed");
    } finally {
      setAiBusy(false);
    }
  }

  function validateForm(): FormErrors {
    const e: FormErrors = {};
    const sport_id = sports.find((s) => s.code === form.sport_code)?.id;
    if (!sport_id) e.sport_code = "Sport is required";
    if (!form.bet_type) e.bet_type = "Bet type is required";
    if (!form.title.trim()) e.title = "Title is required";
    else if (form.title.trim().length > 200) e.title = "Title must be ≤ 200 characters";
    if (!form.game_start_time) e.game_start_time = "Game start time is required";
    if (form.odds === "" || form.odds == null) e.odds = "Odds are required";
    else if (Number.isNaN(Number(form.odds))) e.odds = "Odds must be a number";
    const conf = Number(form.confidence_score);
    if (Number.isNaN(conf) || conf < 0 || conf > 100) e.confidence_score = "0–100";
    const units = Number(form.unit_rating);
    if (Number.isNaN(units) || units < 1 || units > 5) e.unit_rating = "1–5";
    if (form.line !== "" && Number.isNaN(Number(form.line))) e.line = "Must be a number";
    return e;
  }

  async function saveForm(targetStatus?: "draft" | "approved" | "published") {
    const eMap = validateForm();
    setErrors(eMap);
    if (Object.keys(eMap).length) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    const sport_id = sports.find((s) => s.code === form.sport_code)!.id;
    const status = targetStatus ?? (form.status as any);

    // Block publish without manual review
    const totals = computeTotals(form);
    const manualSum = form.injury_impact + form.recent_form + form.matchup_edge + form.data_quality;
    if (status === "published" && manualSum === 0 && !form.manual_reviewed) {
      toast.error("Manual review required before publishing.");
      return;
    }
    setSavingState(targetStatus ?? null);

    const matchupText = form.teams || [
      teams_name(leagueTeams, form.away_team_id),
      teams_name(leagueTeams, form.home_team_id),
    ].filter(Boolean).join(" @ ");

    const payload: any = {
      sport_id,
      league_id: form.league_id || null,
      bet_type: form.bet_type,
      title: form.title,
      teams: matchupText || null,
      line: form.line === "" ? null : Number(form.line),
      odds: form.odds === "" ? null : Number(form.odds),
      sportsbook: form.sportsbook || null,
      confidence_score: totals.total,
      unit_rating: form.units_override !== "" ? Math.max(1, Math.min(5, Number(form.units_override) || totals.units)) : totals.units,
      risk: form.risk,
      ai_summary: form.ai_summary || null,
      ai_reasoning: form.ai_reasoning || null,
      risk_factors: form.risk_factors || null,
      status,
      is_top5: form.is_top5,
      is_free_pick: form.is_free_pick,
      game_start_time: form.game_start_time ? new Date(form.game_start_time).toISOString() : null,
      player_id: form.player_id || null,
      betting_date: form.betting_date || todayBettingDatePT(),
    };
    if (status === "published") payload.published_at = new Date().toISOString();

    const breakdownPayload = {
      line_value: Math.round(form.market_edge),
      market_movement: Math.round(form.line_movement),
      injury_impact: Math.round(form.injury_impact),
      recent_form: Math.round(form.recent_form),
      matchup_edge: Math.round(form.matchup_edge),
      risk_adjustment: Math.round(form.data_quality),
      historical_hit_rate: 0,
      total: totals.total,
    };

    try {
      let pickId = form.id;
      if (form.id) {
        const { error } = await supabase.from("picks").update(payload).eq("id", form.id);
        if (error) throw error;
        await logAction(form.id, targetStatus ? `edit:${targetStatus}` : "edit");
        toast.success(`Pick saved${targetStatus ? ` as ${targetStatus}` : ""}`);
      } else {
        const { data, error } = await supabase.from("picks").insert(payload).select().single();
        if (error) throw error;
        pickId = data?.id;
        if (data) await logAction(data.id, targetStatus ? `create:${targetStatus}` : "create");
        toast.success(`Pick created${targetStatus ? ` (${targetStatus})` : ""}`);
      }
      if (pickId) {
        // Upsert score breakdown (delete existing then insert to avoid needing unique index)
        await supabase.from("pick_score_breakdowns").delete().eq("pick_id", pickId);
        await supabase.from("pick_score_breakdowns").insert({ pick_id: pickId, ...breakdownPayload });
      }
      clearDraft(form.id);
      clearModalSession();
      setOpen(false);
      loadPicks();
    } catch (err: any) {
      toast.error(err.message ?? "Save failed");
    } finally {
      setSavingState(null);
    }
  }

  const [activeTab, setActiveTab] = useState<string>("overview");

  useEffect(() => {
    if (!open || suppressDraftSaveRef.current) return;
    if (draftSaveTimer.current) window.clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = window.setTimeout(() => {
      const safeDraft = { ...form };
      localStorage.setItem(draftKeyFor(form.id), JSON.stringify(safeDraft));
      if (open) saveModalSession({ open: true, mode: form.id ? "edit" : "new", pickId: form.id ?? null });
    }, 700);
    return () => {
      if (draftSaveTimer.current) window.clearTimeout(draftSaveTimer.current);
    };
  }, [open, form]);

  useEffect(() => {
    if (!open) setDraftRestoredNotice(null);
  }, [open]);

  function handleDialogOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      saveModalSession({ open: true, mode: form.id ? "edit" : "new", pickId: form.id ?? null });
      setOpen(true);
      return;
    }
    if (hasUnsavedChanges) {
      if (DEBUG_MODAL) console.info("[admin-modal] close blocked due to unsaved draft");
      setShowDiscardConfirm(true);
      return;
    }
    clearModalSession();
    setOpen(false);
  }

  function discardDraftAndClose() {
    clearDraft(form.id);
    clearModalSession();
    suppressDraftSaveRef.current = true;
    setOpen(false);
    setForm(emptyForm());
    setShowDiscardConfirm(false);
    setTimeout(() => { suppressDraftSaveRef.current = false; }, 0);
  }

  useEffect(() => {
    if (!DEBUG_MODAL) return;
    const navType = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    console.info("[admin-modal] mount", {
      path: location.pathname,
      navType: navType?.type,
      wasDiscarded: (document as any).wasDiscarded ?? false,
    });
    const onVis = () => console.info("[admin-modal] visibility", document.visibilityState);
    const onFocus = () => console.info("[admin-modal] window focus");
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      console.info("[admin-modal] unmount");
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [DEBUG_MODAL, location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/admin") return;
    if (open) return;
    const raw = localStorage.getItem(modalSessionKey);
    if (!raw) return;
    try {
      const session = JSON.parse(raw) as { open?: boolean; mode?: "new" | "edit"; pickId?: string | null; path?: string; updatedAt?: number };
      if (!session.open || session.path !== "/admin" || !session.updatedAt) return;
      if (Date.now() - session.updatedAt > modalTtlMs) {
        clearModalSession();
        return;
      }
      const draftKey = draftKeyFor(session.mode === "edit" ? session.pickId ?? null : null);
      const draft = localStorage.getItem(draftKey);
      if (!draft) return;
      if (DEBUG_MODAL) console.info("[admin-modal] restoring modal session", session);
      if (session.mode === "edit" && session.pickId) {
        const pick = picks.find((p) => p.id === session.pickId);
        if (pick) openEdit(pick);
      } else if (session.mode === "new") {
        openCreate();
      }
    } catch {
      clearModalSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, picks, open]);

  return (
    <div className="container py-8 space-y-6 max-w-[1400px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold">Command Center</h1>
          <p className="text-sm text-muted-foreground mt-1">Daily operations for AleaEdge publishing. <span className="text-muted-foreground/80">All times shown in Pacific Time (PT).</span></p>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          <Button asChild variant="outline" size="sm">
            <Link to="/" target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" />Open Public View</Link>
          </Button>
          <Button onClick={openCreate} size="sm"><Plus className="w-4 h-4 mr-2" />New Pick</Button>
          <div className="flex flex-col items-end gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button onClick={publishToday} disabled={publishing} variant="secondary" size="sm">
                  <Upload className="w-4 h-4 mr-2" />{publishing ? "Publishing…" : "Publish Approved"}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                Bulk publish preserves each pick's Slate Date. {ROLLOVER_HINT}
              </TooltipContent>
            </Tooltip>
            <span className="text-[10px] text-muted-foreground/80">Bulk publish preserves each pick's Slate Date.</span>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto w-full justify-start gap-1 bg-muted/30 p-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="picks">Picks</TabsTrigger>
          <TabsTrigger value="parlays">Parlays</TabsTrigger>
          <TabsTrigger value="articles">Articles</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="api">API / Odds</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-0">
          <AdminOverview onNavigateTab={setActiveTab} />
        </TabsContent>

        <TabsContent value="picks" className="space-y-6 mt-0">
          <AutoTop5Generator />


      {/* Picks Table */}
      <div className="te-card overflow-hidden">
        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border bg-muted/20">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              placeholder="Search title or matchup…"
              className="pl-8 h-9"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterSport} onValueChange={setFilterSport}>
            <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Sport" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterBetType} onValueChange={setFilterBetType}>
            <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Bet Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bet Types</SelectItem>
              {BET_TYPES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterResult} onValueChange={setFilterResult}>
            <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Result" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Results</SelectItem>
              {RESULTS.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="h-9 w-[160px]"
          />
          <Button
            variant={showDeleted ? "default" : "outline"}
            size="sm"
            className="h-9"
            onClick={() => setShowDeleted((v) => !v)}
            title="Toggle deleted picks"
          >
            {showDeleted ? "Showing Deleted" : "Show Deleted"}
          </Button>
          {(filterStatus !== "all" || filterSport !== "all" || filterBetType !== "all" || filterResult !== "all" || filterDate || filterSearch) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterStatus("all"); setFilterSport("all"); setFilterBetType("all"); setFilterResult("all"); setFilterDate(""); setFilterSearch(""); }}>
              Clear
            </Button>
          )}
          <div className="ml-auto text-xs text-muted-foreground">
            {filteredPicks.length} of {picks.length}
          </div>
        </div>

        <TooltipProvider delayDuration={200}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-10">Title</TableHead>
              <TableHead className="h-10">Sport</TableHead>
              <TableHead className="h-10">Type</TableHead>
              <TableHead className="h-10">Odds</TableHead>
              <TableHead className="h-10 cursor-pointer select-none" onClick={() => { setSortBy("confidence_score"); setSortDir(d => sortBy === "confidence_score" && d === "desc" ? "asc" : "desc"); }}>
                Conf / U {sortBy === "confidence_score" ? (sortDir === "desc" ? "↓" : "↑") : ""}
              </TableHead>
              <TableHead className="h-10">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 cursor-help">Status / Slate <Info className="w-3 h-3 opacity-60" /></span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    <div><strong>Slate Date</strong> controls which day's board this pick appears on.</div>
                    <div className="mt-1"><strong>Published At</strong> is an audit timestamp only — it does not affect visibility.</div>
                    <div className="mt-1 opacity-80">{ROLLOVER_HINT}</div>
                  </TooltipContent>
                </Tooltip>
              </TableHead>
              <TableHead className="h-10">Result</TableHead>
              <TableHead className="h-10">Flags</TableHead>
              <TableHead className="h-10 cursor-pointer select-none" onClick={() => { setSortBy("game_start_time"); setSortDir(d => sortBy === "game_start_time" && d === "desc" ? "asc" : "desc"); }}>
                Game {sortBy === "game_start_time" ? (sortDir === "desc" ? "↓" : "↑") : ""}
              </TableHead>
              <TableHead className="h-10 cursor-pointer select-none" onClick={() => { setSortBy("created_at"); setSortDir(d => sortBy === "created_at" && d === "desc" ? "asc" : "desc"); }}>
                Created {sortBy === "created_at" ? (sortDir === "desc" ? "↓" : "↑") : ""}
              </TableHead>
              <TableHead className="h-10 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
            {!loading && filteredPicks.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">{picks.length === 0 ? "No picks yet — click New Pick to create one." : "No picks match these filters."}</TableCell></TableRow>}
            {filteredPicks.map((p) => {
              const meta = SPORT_META[p.sports?.code] ?? SPORT_META.NBA;
              const units = scoreToUnits(p.confidence_score);
              const conf = Math.max(0, Math.min(100, Number(p.confidence_score) || 0));
              return (
                <TableRow key={p.id} className="group hover:bg-muted/30 transition-colors">
                  <TableCell className="max-w-[260px] py-2">
                    <div className="font-medium truncate text-sm">{p.title}</div>
                    <div className="text-xs text-muted-foreground/70 truncate">{p.teams}</div>
                  </TableCell>
                  <TableCell className="py-2"><span className={`te-chip border ${meta.color}`}>{meta.icon} {p.sports?.code}</span></TableCell>
                  <TableCell className="text-xs py-2">{BET_LABEL[p.bet_type]}</TableCell>
                  <TableCell className="text-xs py-2 font-mono">{formatOdds(p.odds)}</TableCell>
                  <TableCell className="py-2 min-w-[90px]">
                    <div className="text-xs font-medium">{p.confidence_score} / {units}u</div>
                    <div className="mt-1 h-1 w-16 rounded-full bg-muted overflow-hidden">
                      <div
                        className={cn(
                          "h-full transition-all",
                          conf >= 75 ? "bg-success" : conf >= 50 ? "bg-warning" : "bg-danger"
                        )}
                        style={{ width: `${conf}%` }}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="py-2 min-w-[140px]">
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={p.status} />
                      <SlateChip bettingDate={p.betting_date} />
                      <span className="text-[10px] text-muted-foreground/70 whitespace-nowrap">
                        {p.published_at ? <>Pub {format(new Date(p.published_at), "MMM d, h:mma")}</> : <span className="opacity-60">Not published</span>}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2"><ResultBadge result={p.result} /></TableCell>
                  <TableCell className="py-2">
                    <div className="flex gap-1">
                      <FlagToggle
                        active={!!p.is_top5}
                        icon={<Trophy className="w-3 h-3" />}
                        label="Top 5"
                        tooltip={p.is_top5 ? "Remove from Top 5" : "Mark as Top 5"}
                        onClick={() => quickUpdate(p.id, { is_top5: !p.is_top5 }, p.is_top5 ? "unmark_top5" : "mark_top5")}
                        activeClass="bg-accent/15 text-accent border-accent/40"
                      />
                      <FlagToggle
                        active={!!p.is_free_pick}
                        icon={<Star className="w-3 h-3" />}
                        label="Free"
                        tooltip={p.is_free_pick ? "Remove Free Pick" : "Mark as Free Pick"}
                        onClick={() => setFreePick(p.id, !!p.is_free_pick)}
                        activeClass="bg-success/15 text-success border-success/40"
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground py-2 whitespace-nowrap">
                    {p.game_start_time ? format(new Date(p.game_start_time), "MMM d, h:mma") : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground py-2 whitespace-nowrap">
                    {p.created_at ? format(new Date(p.created_at), "MMM d, h:mma") : "—"}
                  </TableCell>
                  <TableCell className="text-right py-2">
                    <div className="flex flex-wrap gap-1 justify-end items-center">
                      {/* Primary actions */}
                      {!p.deleted_at && p.status === "draft" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button size="sm" className="h-8 text-white hover:opacity-90 [background-color:hsl(217_91%_60%)]" onClick={() => quickUpdate(p.id, { status: "approved" }, "approve")}>
                              <Check className="w-3 h-3 mr-1" />Approve
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Move to Approved</TooltipContent>
                        </Tooltip>
                      )}
                      {!p.deleted_at && p.status === "approved" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              className="h-8 bg-success text-white hover:bg-success/90"
                              onClick={() => setConfirmAction({
                                title: "Publish pick?",
                                description: `"${p.title}" will become visible to users.`,
                                confirmLabel: "Publish",
                                onConfirm: () => quickUpdate(p.id, { status: "published", published_at: new Date().toISOString() }, "publish"),
                              })}
                            >
                              <Send className="w-3 h-3 mr-1" />Publish
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Make visible to users</TooltipContent>
                        </Tooltip>
                      )}
                      {!p.deleted_at && p.status === "published" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              onClick={() => setConfirmAction({
                                title: "Unpublish pick?",
                                description: `"${p.title}" will be hidden from users.`,
                                confirmLabel: "Unpublish",
                                onConfirm: () => quickUpdate(p.id, { status: "approved", published_at: null }, "unpublish"),
                              })}
                            >
                              <EyeOff className="w-3 h-3 mr-1" />Unpublish
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Hide from users</TooltipContent>
                        </Tooltip>
                      )}

                      {/* Secondary */}
                      {!p.deleted_at && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8" onClick={() => openEdit(p)}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit pick</TooltipContent>
                        </Tooltip>
                      )}
                      {!p.deleted_at && p.status === "draft" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-danger hover:text-danger"
                              onClick={() => setConfirmAction({
                                title: "Reject pick?",
                                description: `"${p.title}" will be marked as rejected.`,
                                confirmLabel: "Reject",
                                variant: "destructive",
                                onConfirm: () => quickUpdate(p.id, { status: "rejected" }, "reject"),
                              })}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reject pick</TooltipContent>
                        </Tooltip>
                      )}

                      {/* Settle results */}
                      {!p.deleted_at && p.status === "published" && p.result === "pending" && (
                        <div className="flex gap-1 ml-1 pl-1 border-l border-border">
                          {(["won","lost","pushed"] as const).map((r) => (
                            <Button
                              key={r}
                              size="sm"
                              variant="outline"
                              className={cn(
                                "h-8 px-2",
                                r === "won" && "text-success hover:bg-success/10 hover:text-success border-success/30",
                                r === "lost" && "text-danger hover:bg-danger/10 hover:text-danger border-danger/30",
                                r === "pushed" && "text-muted-foreground hover:bg-muted",
                              )}
                              onClick={() => setConfirmAction({
                                title: `Mark result as ${r === "pushed" ? "Push" : r}?`,
                                description: `Settle "${p.title}" — units delta will be ${r === "won" ? `+${(p.unit_rating * (p.odds == null ? 1 : p.odds > 0 ? p.odds / 100 : 100 / Math.abs(p.odds))).toFixed(2)}` : r === "lost" ? `-${p.unit_rating}` : 0}.`,
                                confirmLabel: r === "pushed" ? "Push" : r.charAt(0).toUpperCase() + r.slice(1),
                                variant: r === "lost" ? "destructive" : "default",
                                onConfirm: () => settle(p.id, r, p.unit_rating, p.odds),
                              })}
                            >{r === "pushed" ? "Push" : r.charAt(0).toUpperCase() + r.slice(1)}</Button>
                          ))}
                        </div>
                      )}

                      {/* Reset settled result back to pending */}
                      {!p.deleted_at && p.status === "published" && p.result !== "pending" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 ml-1 text-muted-foreground hover:bg-muted"
                              onClick={() => setConfirmAction({
                                title: "Reset result to pending?",
                                description: `"${p.title}" will be reverted to pending and its units delta cleared. Platform record will update accordingly.`,
                                confirmLabel: "Reset to Pending",
                                variant: "default",
                                onConfirm: () => quickUpdate(p.id, { result: "pending", units_delta: 0 }, "reset:pending"),
                              })}
                            >Reset</Button>
                          </TooltipTrigger>
                          <TooltipContent>Revert result back to pending</TooltipContent>
                        </Tooltip>
                      )}

                      {/* Delete / Restore */}
                      {!p.deleted_at ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-danger hover:bg-danger/10 hover:text-danger border-danger/30 ml-1"
                              onClick={() => setConfirmAction({
                                title: "Delete pick?",
                                description: `"${p.title}" will be moved to deleted (soft delete). It can be restored later.`,
                                confirmLabel: "Delete",
                                variant: "destructive",
                                onConfirm: () => softDelete(p.id),
                              })}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete pick (soft)</TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8" onClick={() => restorePick(p.id)}>
                              <RotateCcw className="w-3 h-3 mr-1" />Restore
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Restore deleted pick</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </TooltipProvider>
      </div>
        </TabsContent>

        <TabsContent value="parlays" className="space-y-6 mt-0">
          <ParlayOfDayManager sports={sports} />
        </TabsContent>

        <TabsContent value="articles" className="space-y-4 mt-0">
          <Card className="te-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <FileText className="w-5 h-5 text-accent" />Articles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">Manage AI-generated and manual pick articles, slugs, and publishing.</p>
              <Button asChild><Link to="/admin/articles">Open Articles Manager <ExternalLink className="w-4 h-4 ml-2" /></Link></Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4 mt-0">
          <Card className="te-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <UsersIcon className="w-5 h-5 text-accent" />Users & Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">View users, manage roles, and grant Pro subscription overrides.</p>
              <Button asChild><Link to="/admin/users">Open User Manager <ExternalLink className="w-4 h-4 ml-2" /></Link></Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api" className="space-y-6 mt-0">
          <ApiTestLab sports={sports} />
        </TabsContent>

        <TabsContent value="settings" className="space-y-6 mt-0">
          <Card className="te-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <SettingsIcon className="w-5 h-5 text-accent" />Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Operational settings and integrations will live here. (Distribution, scheduling, defaults — coming soon.)</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PickDialog
        open={open} onOpenChange={handleDialogOpenChange} form={form} setForm={setForm}
        sportLeagues={sportLeagues} leagueTeams={leagueTeams} players={players}
        onAi={aiAssist} aiBusy={aiBusy} onSave={saveForm}
        errors={errors} savingState={savingState} restoredNotice={draftRestoredNotice}
      />

      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>Your pick draft has unsaved changes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDiscardConfirm(false)}>Keep editing</AlertDialogCancel>
            <AlertDialogAction className="bg-danger text-white hover:bg-danger/90" onClick={discardDraftAndClose}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmAction} onOpenChange={(o) => { if (!o) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmAction?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(confirmAction?.variant === "destructive" && "bg-danger text-white hover:bg-danger/90")}
              onClick={async () => {
                const a = confirmAction;
                setConfirmAction(null);
                if (a) await a.onConfirm();
              }}
            >
              {confirmAction?.confirmLabel ?? "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>

  );
}

function teams_name(list: TeamRow[], id: string) {
  return list.find((t) => t.id === id)?.name ?? "";
}

function PickDialog({ open, onOpenChange, form, setForm, sportLeagues, leagueTeams, players, onAi, aiBusy, onSave, errors, savingState, restoredNotice }: any) {
  const update = (k: keyof FormState, v: any) => setForm((f: FormState) => ({ ...f, [k]: v }));
  const isPlayerProp = form.bet_type === "player_prop";
  const e: FormErrors = errors ?? {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{form.id ? "Edit Pick" : "Create Pick"}</DialogTitle>
          {form.id && form.updated_at && (
            <div className="text-xs text-muted-foreground">Last updated {format(new Date(form.updated_at), "MMM d, yyyy h:mma")}</div>
          )}
          {restoredNotice && <div className="text-xs text-primary">{restoredNotice}</div>}
        </DialogHeader>

        <div className="space-y-6">
          {/* Section 1: Game Info */}
          <Section title="Game Info">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sport" required error={e.sport_code}>
                <Select value={form.sport_code} onValueChange={(v) => { update("sport_code", v); update("league_id", ""); update("home_team_id", ""); update("away_team_id", ""); }}>
                  <SelectTrigger className={cn(e.sport_code && "border-danger")}><SelectValue /></SelectTrigger>
                  <SelectContent>{SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="League">
                <Select value={form.league_id} onValueChange={(v) => update("league_id", v)}>
                  <SelectTrigger><SelectValue placeholder="Select league" /></SelectTrigger>
                  <SelectContent>{sportLeagues.map((l: LeagueRow) => <SelectItem key={l.id} value={l.id}>{l.short_name ?? l.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Home Team">
                {leagueTeams.length ? (
                  <Select value={form.home_team_id} onValueChange={(v) => update("home_team_id", v)}>
                    <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                    <SelectContent>{leagueTeams.map((t: TeamRow) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Input placeholder="Home team" value={form.home_team_id} onChange={(ev) => update("home_team_id", ev.target.value)} />}
              </Field>
              <Field label="Away Team">
                {leagueTeams.length ? (
                  <Select value={form.away_team_id} onValueChange={(v) => update("away_team_id", v)}>
                    <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                    <SelectContent>{leagueTeams.map((t: TeamRow) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Input placeholder="Away team" value={form.away_team_id} onChange={(ev) => update("away_team_id", ev.target.value)} />}
              </Field>
              <Field label="Game Start Time" required error={e.game_start_time} full>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("flex-1 justify-start text-left font-normal", !form.game_start_time && "text-muted-foreground", e.game_start_time && "border-danger")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {(() => { const d = form.game_start_time ? new Date(form.game_start_time) : null; return d && !isNaN(d.getTime()) ? format(d, "PPP") : <span>Pick a date</span>; })()}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-50" align="start">
                      <Calendar
                        mode="single"
                        selected={(() => { const d = form.game_start_time ? new Date(form.game_start_time) : null; return d && !isNaN(d.getTime()) ? d : undefined; })()}
                        onSelect={(d) => {
                          if (!d) return;
                          const time = form.game_start_time?.slice(11, 16) || "19:00";
                          update("game_start_time", `${format(d, "yyyy-MM-dd")}T${time}`);
                        }}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    className="w-32"
                    value={form.game_start_time?.slice(11, 16) || ""}
                    onChange={(ev) => {
                      const date = form.game_start_time?.slice(0, 10) || format(new Date(), "yyyy-MM-dd");
                      update("game_start_time", `${date}T${ev.target.value}`);
                    }}
                  />
                </div>
              </Field>
              <Field label="Matchup (free text override)" full>
                <Input placeholder="e.g. Lakers @ Warriors" value={form.teams} onChange={(ev) => update("teams", ev.target.value)} />
              </Field>
              <Field label="Slate Date (PT Betting Day)" required full hint="Controls which slate this pick appears under. Publishing time is separate.">
                <Input type="date" value={form.betting_date} onChange={(ev) => update("betting_date", ev.target.value)} />
              </Field>
            </div>
          </Section>

          {/* Section 2: Bet Details */}
          <Section title="Bet Details">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Bet Type" required error={e.bet_type}>
                <Select value={form.bet_type} onValueChange={(v) => update("bet_type", v)}>
                  <SelectTrigger className={cn(e.bet_type && "border-danger")}><SelectValue /></SelectTrigger>
                  <SelectContent>{BET_TYPES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {isPlayerProp && (
                <Field label="Player Name">
                  <Input value={form.player_name} onChange={(ev) => update("player_name", ev.target.value)} placeholder="e.g. LeBron James" />
                </Field>
              )}
              <Field label="Pick Title" required error={e.title} full>
                <Input value={form.title} onChange={(ev) => update("title", ev.target.value)} placeholder="e.g. Lakers ML" className={cn(e.title && "border-danger")} />
              </Field>
              <Field label="Selection" full>
                <Input value={form.selection} onChange={(ev) => update("selection", ev.target.value)} placeholder="e.g. Over 24.5 Points" />
              </Field>
              <Field label="Line" error={e.line}>
                <Input type="number" step="0.5" value={form.line} onChange={(ev) => update("line", ev.target.value)} className={cn(e.line && "border-danger")} />
              </Field>
              <Field label="Odds (American)" required error={e.odds}>
                <Input type="number" value={form.odds} onChange={(ev) => update("odds", ev.target.value)} placeholder="-110" className={cn(e.odds && "border-danger")} />
              </Field>
              <Field label="Sportsbook / Source" full>
                <Input value={form.sportsbook} onChange={(ev) => update("sportsbook", ev.target.value)} placeholder="DraftKings" />
              </Field>
            </div>
          </Section>

          {/* Section 3: Scoring */}
          <ScoringSection form={form} update={update} />

          <Section title="Risk">
            <div className="grid grid-cols-1 gap-3">
              <Field label="Risk">
                <Select value={form.risk} onValueChange={(v) => update("risk", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{RISKS.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </Section>


          {/* Section 4: Reasoning */}
          <Section
            title="Reasoning"
            action={
              <Button type="button" size="sm" onClick={onAi} disabled={aiBusy} className="bg-gradient-primary text-primary-foreground">
                <Sparkles className="w-4 h-4 mr-2" />{aiBusy ? "Generating…" : "Generate AI Reasoning"}
              </Button>
            }
          >
            <Field label="AI Summary" full><Textarea rows={2} value={form.ai_summary} onChange={(ev) => update("ai_summary", ev.target.value)} /></Field>
            <Field label="Detailed Reasoning" full><Textarea rows={5} value={form.ai_reasoning} onChange={(ev) => update("ai_reasoning", ev.target.value)} /></Field>
            <Field label="Risk Factors" full><Textarea rows={2} value={form.risk_factors} onChange={(ev) => update("risk_factors", ev.target.value)} /></Field>
          </Section>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={!!savingState}>Cancel</Button>
          <Button variant="outline" onClick={() => onSave("draft")} disabled={!!savingState}>
            {savingState === "draft" ? "Saving…" : "Save as Draft"}
          </Button>
          <Button onClick={() => onSave("approved")} disabled={!!savingState} className="text-white [background-color:hsl(217_91%_60%)] hover:opacity-90">
            {savingState === "approved" ? "Saving…" : "Save & Approve"}
          </Button>
          <Button onClick={() => onSave("published")} disabled={!!savingState} className="bg-success text-white hover:bg-success/90">
            {savingState === "published" ? "Publishing…" : "Save & Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b border-border pb-1.5">
        <h3 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, full, required, error, hint }: { label: string; children: React.ReactNode; full?: boolean; required?: boolean; error?: string; hint?: string }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className={cn("text-xs", error ? "text-danger" : "text-muted-foreground")}>
        {label}{required && <span className="text-danger ml-0.5">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {hint && !error && <p className="mt-1 text-[11px] text-muted-foreground/80">{hint}</p>}
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-warning/15 text-warning border-warning/40",
  approved: "[background-color:hsl(217_91%_60%/0.15)] [color:hsl(217_91%_70%)] [border-color:hsl(217_91%_60%/0.4)]",
  published: "bg-success/15 text-success border-success/40",
  rejected: "bg-danger/15 text-danger border-danger/40",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? "bg-secondary text-secondary-foreground border-border";
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize", cls)}>
      {status}
    </span>
  );
}

function SlateChip({ bettingDate }: { bettingDate: string | null | undefined }) {
  const rel = slateRelative(bettingDate);
  const label = slateLabel(bettingDate);
  const cls =
    rel === "today" ? "bg-success/15 text-success border-success/40" :
    rel === "tomorrow" ? "bg-primary/15 text-primary border-primary/40" :
    rel === "yesterday" ? "bg-muted text-muted-foreground border-border" :
    "bg-secondary/50 text-foreground/80 border-border";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium cursor-help whitespace-nowrap w-fit", cls)}>
          <CalendarIcon className="w-2.5 h-2.5" />
          Slate · {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="text-xs">
        Slate Date {bettingDate ?? "—"} (PT). Controls where this pick appears on the public board.
      </TooltipContent>
    </Tooltip>
  );
}

const RESULT_STYLES: Record<string, string> = {
  won: "bg-success/15 text-success border-success/40",
  lost: "bg-danger/15 text-danger border-danger/40",
  pushed: "bg-muted text-muted-foreground border-border",
  pending: "bg-secondary/40 text-muted-foreground border-border",
};

function ResultBadge({ result }: { result: string }) {
  if (!result) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = RESULT_STYLES[result] ?? RESULT_STYLES.pending;
  const label = result === "pushed" ? "Push" : result;
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize", cls)}>
      {label}
    </span>
  );
}

function FlagToggle({
  active, icon, label, tooltip, onClick, activeClass,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void;
  activeClass: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors",
            active ? activeClass : "bg-transparent text-muted-foreground border-border hover:bg-muted/50"
          )}
        >
          {icon}
          <span>{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function ScoreSlider({
  label, value, onChange, max, readOnly, hint,
}: {
  label: string;
  value: number;
  onChange?: (n: number) => void;
  max: number;
  readOnly?: boolean;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium flex items-center gap-1">
          {label}
          {readOnly && <Lock className="w-3 h-3 text-muted-foreground" />}
        </span>
        <span className="tabular-nums text-muted-foreground">
          {Math.round(value)} <span className="opacity-60">/ {max}</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Slider
          value={[Math.min(max, Math.max(0, value))]}
          min={0}
          max={max}
          step={1}
          onValueChange={(v) => !readOnly && onChange?.(v[0])}
          disabled={readOnly}
          className={cn(readOnly && "opacity-70 pointer-events-none")}
        />
        <Input
          type="number"
          min={0}
          max={max}
          value={Math.round(value)}
          onChange={(ev) => !readOnly && onChange?.(Math.max(0, Math.min(max, Number(ev.target.value) || 0)))}
          disabled={readOnly}
          className="w-16 h-8 text-xs"
        />
      </div>
      {hint && <p className="text-[11px] text-muted-foreground flex items-start gap-1"><Info className="w-3 h-3 mt-0.5 shrink-0" />{hint}</p>}
    </div>
  );
}

function ScoringSection({ form, update }: { form: FormState; update: (k: keyof FormState, v: any) => void }) {
  const totals = computeTotals(form);
  const manualSum = form.injury_impact + form.recent_form + form.matchup_edge + form.data_quality;
  const needsReview = manualSum === 0 && !form.manual_reviewed;

  return (
    <Section title="Confidence Scoring">
      <div className="space-y-4">
        {needsReview && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Manual review required before publishing</AlertTitle>
            <AlertDescription>
              Set the Injury, Form, Matchup, and Data Quality scores below — or confirm them at zero — before this pick can be published.
            </AlertDescription>
          </Alert>
        )}

        {/* Auto Scanner Score */}
        <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-muted-foreground" />
              <h4 className="font-semibold text-sm">Auto Scanner Score</h4>
            </div>
            <Badge variant="secondary" className="tabular-nums">{totals.auto} / 45</Badge>
          </div>
          <ScoreSlider
            label="Market Edge"
            value={form.market_edge}
            max={30}
            readOnly
            hint="Computed from line-shopping vs. consensus across books."
          />
          <ScoreSlider
            label="Line Movement"
            value={form.line_movement}
            max={15}
            readOnly
            hint={
              form.line_movement_neutral
                ? "Neutral default (7/15) — insufficient snapshot history to detect movement."
                : "Based on opening vs. current line trajectory."
            }
          />
        </div>

        {/* Manual Review Score */}
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Pencil className="w-4 h-4 text-primary" />
              <h4 className="font-semibold text-sm">Manual Review Score</h4>
            </div>
            <Badge className="tabular-nums">{totals.manual} / 55</Badge>
          </div>
          <ScoreSlider label="Injury / Lineup Impact" value={form.injury_impact} max={15}
            onChange={(n) => { update("injury_impact", n); update("manual_reviewed", true); }} />
          <ScoreSlider label="Recent Form" value={form.recent_form} max={15}
            onChange={(n) => { update("recent_form", n); update("manual_reviewed", true); }} />
          <ScoreSlider label="Matchup Edge" value={form.matchup_edge} max={15}
            onChange={(n) => { update("matchup_edge", n); update("manual_reviewed", true); }} />
          <ScoreSlider label="Data Quality / Risk" value={form.data_quality} max={10}
            onChange={(n) => { update("data_quality", n); update("manual_reviewed", true); }} />
          {needsReview && (
            <button
              type="button"
              onClick={() => update("manual_reviewed", true)}
              className="text-xs text-primary hover:underline"
            >
              Confirm reviewed (keep all manual scores at 0)
            </button>
          )}
        </div>

        {/* Final */}
        <div className="rounded-lg border-2 border-primary bg-gradient-to-br from-primary/10 to-transparent p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Final Confidence</div>
              <div className="text-3xl font-bold tabular-nums">{totals.total}<span className="text-base text-muted-foreground"> / 100</span></div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Units {form.units_override !== "" && <span className="text-primary">(manual)</span>}</div>
              <div className="text-3xl font-bold tabular-nums flex items-center gap-1 justify-end">
                {form.units_override !== "" ? form.units_override : totals.units}<span className="text-base text-muted-foreground">u</span>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Override units:</Label>
            <Select
              value={form.units_override === "" ? "auto" : form.units_override}
              onValueChange={(v) => update("units_override", v === "auto" ? "" : v)}
            >
              <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto ({totals.units}u)</SelectItem>
                {[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)}>{n}u</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            Auto {totals.auto}/45 + Manual {totals.manual}/55. Auto units: ≥85→5, ≥70→4, ≥55→3, ≥40→2, else 1.
          </div>
        </div>
      </div>
    </Section>
  );
}
